import { createContext, useContext, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import AlertModal from "./AlertModal";
import { setSessionAlertFunction } from "../../utils/SessionAlertHelper";
import CryptoJS from "crypto-js";

const SessionContext = createContext();
const SECRET_KEY = process.env.REACT_APP_SECRET_KEY || "default-secret-key";

export const SessionProvider = ({ children }) => {
  const [alert, setAlert] = useState(null);
  const [user, setUser] = useState(null);
  const [permissions, setPermissions] = useState({}); // store permissions
  const navigate = useNavigate();

   // Decrypt stored permissions
  const decryptPermissions = (encryptedPermissions) => {
    try {
      if (!encryptedPermissions) return {};
      const bytes = CryptoJS.AES.decrypt(encryptedPermissions, SECRET_KEY);
      return JSON.parse(bytes.toString(CryptoJS.enc.Utf8));
    } catch (err) {
      console.error("Failed to decrypt permissions:", err);
      return {};
    }
  };

  // Encrypt permissions before storing
  const encryptPermissions = (permissions) => {
    try {
      return CryptoJS.AES.encrypt(
        JSON.stringify(permissions),
        SECRET_KEY
      ).toString();
    } catch (err) {
      console.error("Failed to encrypt permissions:", err);
      return null;
    }
  };

  // Load user & permissions from localStorage on mount
  useEffect(() => {
    const storedUser = JSON.parse(localStorage.getItem("user")) || null;
     const storedEncryptedPermissions = localStorage.getItem("permissions");
    setUser(storedUser);
 setPermissions(decryptPermissions(storedEncryptedPermissions));
  }, []);

  const labelToKeyMap = {
    Dashboard: "dashboard",
    Orders: "order",
    International: "international",
    Brands: "brand",
    Products: "product",
    Inventory: "inventory",
    "Sim swap": "simSwap",
    Customers: "customer",
    "App banner": "appBanner",
    "Referral Code": "referralCode",
    "Manage users": "manageUser",
  };

  //  Permission checker
  const hasPermission = (label, action) => {
    // If user has full access
  if (permissions === "All Access Granted") {
    return true;
  }
  
    const key = labelToKeyMap[label];
    return permissions[key]?.includes(action) || false;
  };

  const showSessionAlert = (message) => {
    setAlert(message);
  };

  const handleConfirm = () => {
    setAlert(null);
    navigate("/signin");
  };

  setSessionAlertFunction(showSessionAlert); // this initializes the helper

  return (
    <SessionContext.Provider
      value={{ user, setUser, permissions, setPermissions: (newPermissions) => {
          const encrypted = encryptPermissions(newPermissions);
          if (encrypted) {
            localStorage.setItem("permissions", encrypted);
          }
          setPermissions(newPermissions);
        }, hasPermission }}
    >
      {children}
      {alert && <AlertModal message={alert} onConfirm={handleConfirm} />}
    </SessionContext.Provider>
  );
};

export const useSession = () => useContext(SessionContext);
